import { computed, type Ref } from "vue";
import type { RunCanvasNode, RunCanvasEdge } from "@/types";

/**
 * Derives the VueFlow node/edge props for a read-only run canvas.
 *
 * This is the history slice of BuilderView's `enhancedNodes` — it stamps the
 * per-node canvas metadata (leaf detection, canvas mode, flow direction) onto
 * whatever laid-out nodes it's handed, and leaves every editing handler
 * (`onAddService`, `onRunService`, …) unset, because a run is read-only.
 *
 * There is deliberately no live-execution overlay: run status and per-service
 * duration ride on `node.data.run` (a {@link ServiceHistory}) via the source
 * derivation, and live frames arrive by replacing that run — not through a
 * separate `isRunning` flag (removed with the sequential-engine rework).
 *
 * @param nodes  Laid-out history nodes (positions already assigned). RunCanvas
 *               passes a local, re-layoutable copy so LR/drag don't mutate the
 *               shared computed.
 * @param edges  History edges — already carry status-derived styling/animation.
 */
export function useRunCanvasNodes(
  nodes: Ref<RunCanvasNode[]>,
  edges: Ref<RunCanvasEdge[]>,
  flowDirection: Ref<"TB" | "LR">
) {
  // Leaf = a node nothing points out of. Derived from the edge sources, so it
  // reflects the run's actual shape rather than array position.
  const leafNodeIds = computed(() => {
    const nodesWithChildren = new Set(edges.value.map((edge) => edge.source));
    return new Set(
      nodes.value
        .filter((node) => !nodesWithChildren.has(node.id))
        .map((node) => node.id)
    );
  });

  // Root = a node nothing points into (mirror of leaf, from the edge targets).
  // The history derivation doesn't stamp `isFirstNode` (that's an editor-only
  // field from rootServiceId), so we derive it here from the run's actual shape,
  // which is what gates the top handle off the first node.
  const rootNodeIds = computed(() => {
    const nodesWithParents = new Set(edges.value.map((edge) => edge.target));
    return new Set(
      nodes.value
        .filter((node) => !nodesWithParents.has(node.id))
        .map((node) => node.id)
    );
  });

  const enhancedNodes = computed(() =>
    nodes.value.map((node) => ({
      ...node,
      data: {
        ...node.data,
        isLeafNode: leafNodeIds.value.has(node.id),
        isFirstNode: rootNodeIds.value.has(node.id),
        canvasMode: "history" as const,
        flowDirection: flowDirection.value,
        // Read-only: no add / run / toggle / delete / rename / duplicate handlers.
      },
    }))
  );

  const currentEdges = computed(() => edges.value);

  return { enhancedNodes, currentEdges, leafNodeIds };
}
